import type { FormSourceFilter, FormStatusFilter } from "./formListFilters";

export const FORMS_LIST_PREFERENCES_KEY = "chmforms.admin.formsList.v1";

export type FormsListSortBy =
  | "createdAt"
  | "updatedAt"
  | "name"
  | "answersCount";
export type FormsListSortDir = "asc" | "desc";

export interface FormsListPreferences {
  search: string;
  status: FormStatusFilter;
  source: FormSourceFilter;
  /** null = все организации */
  organisationId: number | null;
  sortBy: FormsListSortBy;
  sortDir: FormsListSortDir;
  perPage: number;
}

export const DEFAULT_FORMS_LIST_PREFERENCES: FormsListPreferences = {
  search: "",
  status: "all",
  source: "all",
  organisationId: null,
  sortBy: "createdAt",
  sortDir: "desc",
  perPage: 15,
};

const SORT_BY_VALUES = new Set<FormsListSortBy>([
  "createdAt",
  "updatedAt",
  "name",
  "answersCount",
]);
const SORT_DIR_VALUES = new Set<FormsListSortDir>(["asc", "desc"]);
const STATUS_VALUES = new Set<FormStatusFilter>(["all", "open", "closed"]);
const SOURCE_VALUES = new Set<FormSourceFilter>(["all", "native", "yandex"]);
const PER_PAGE_VALUES = new Set([15, 25, 50, 100]);

export interface FormsListSortOption {
  value: `${FormsListSortBy}:${FormsListSortDir}`;
  label: string;
}

export const FORMS_LIST_SORT_OPTIONS: FormsListSortOption[] = [
  { value: "createdAt:desc", label: "Сначала новые" },
  { value: "createdAt:asc", label: "Сначала старые" },
  { value: "updatedAt:desc", label: "Сначала обновлённые" },
  { value: "name:asc", label: "Название А→Я" },
  { value: "name:desc", label: "Название Я→А" },
  { value: "answersCount:desc", label: "Больше ответов" },
  { value: "answersCount:asc", label: "Меньше ответов" },
];

export function parseSortOptionValue(
  value: string,
): Pick<FormsListPreferences, "sortBy" | "sortDir"> | null {
  const [sortBy, sortDir] = value.split(":") as [
    FormsListSortBy,
    FormsListSortDir,
  ];

  if (!SORT_BY_VALUES.has(sortBy) || !SORT_DIR_VALUES.has(sortDir)) {
    return null;
  }

  return { sortBy, sortDir };
}

export function toSortOptionValue(
  sortBy: FormsListSortBy,
  sortDir: FormsListSortDir,
): FormsListSortOption["value"] {
  return `${sortBy}:${sortDir}`;
}

function sanitizePreferences(value: unknown): Partial<FormsListPreferences> {
  if (!value || typeof value !== "object") return {};

  const raw = value as Record<string, unknown>;
  const next: Partial<FormsListPreferences> = {};

  if (typeof raw.search === "string") next.search = raw.search;

  if (
    typeof raw.status === "string" &&
    STATUS_VALUES.has(raw.status as FormStatusFilter)
  ) {
    next.status = raw.status as FormStatusFilter;
  }

  if (
    typeof raw.source === "string" &&
    SOURCE_VALUES.has(raw.source as FormSourceFilter)
  ) {
    next.source = raw.source as FormSourceFilter;
  }

  if (raw.organisationId === null) {
    next.organisationId = null;
  } else if (
    typeof raw.organisationId === "number" &&
    Number.isFinite(raw.organisationId)
  ) {
    next.organisationId = Math.trunc(raw.organisationId);
  }

  if (
    typeof raw.sortBy === "string" &&
    SORT_BY_VALUES.has(raw.sortBy as FormsListSortBy)
  ) {
    next.sortBy = raw.sortBy as FormsListSortBy;
  }

  if (
    typeof raw.sortDir === "string" &&
    SORT_DIR_VALUES.has(raw.sortDir as FormsListSortDir)
  ) {
    next.sortDir = raw.sortDir as FormsListSortDir;
  }

  if (typeof raw.perPage === "number" && PER_PAGE_VALUES.has(raw.perPage)) {
    next.perPage = raw.perPage;
  }

  return next;
}

export function loadFormsListPreferences(): FormsListPreferences {
  if (typeof window === "undefined") {
    return { ...DEFAULT_FORMS_LIST_PREFERENCES };
  }

  try {
    const raw = localStorage.getItem(FORMS_LIST_PREFERENCES_KEY);
    if (!raw) return { ...DEFAULT_FORMS_LIST_PREFERENCES };

    return {
      ...DEFAULT_FORMS_LIST_PREFERENCES,
      ...sanitizePreferences(JSON.parse(raw)),
    };
  } catch {
    return { ...DEFAULT_FORMS_LIST_PREFERENCES };
  }
}

export function saveFormsListPreferences(
  preferences: FormsListPreferences,
): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(
      FORMS_LIST_PREFERENCES_KEY,
      JSON.stringify(preferences),
    );
  } catch {
    // ignore quota / private mode
  }
}
